import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { CalendarPlus, MapPin, Navigation, ArrowRight } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { CTA15Min } from '@/components/sections/cta-15min'
import { MaroochydoreAppointmentCards } from '@/components/sections/maroochydore-appointment-cards'
import { getGeneralHealthAppointments } from '@/data/appointments'
import { TableOfContents } from '@/components/sections/table-of-contents'

const getMapboxStaticImageUrl = (coordinates: string, width: number = 600, height: number = 300) => {
  const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()))
  
  const env = import.meta.env as { VITE_MAPBOX_TOKEN?: string; VITE_MAPBOX_ACCESS_TOKEN?: string }
  const accessToken = env.VITE_MAPBOX_TOKEN || env.VITE_MAPBOX_ACCESS_TOKEN
  
  if (!accessToken) {
    console.warn('Mapbox token not found for static images')
    return ''
  }
  
  const styleId = 'streets-v12'
  const username = 'mapbox'
  const zoom = 14
  const pinColor = 'ff3333'
  const marker = `pin-l+${pinColor}(${lng},${lat})`
  
  return `https://api.mapbox.com/styles/v1/${username}/${styleId}/static/${marker}/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${accessToken}`
}

const openDirections = (address: string, coordinates?: string) => {
  const encodedAddress = encodeURIComponent(address)
  
  let coords = ''
  if (coordinates) {
    const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()))
    coords = `${lat},${lng}`
  }
  
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  
  let mapUrl: string
  
  if (isIOS) {
    mapUrl = `maps://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`
    window.location.href = mapUrl
    
    setTimeout(() => {
      const fallbackUrl = coords 
        ? `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
    }, 1000)
  } else {
    mapUrl = coords
      ? `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=driving`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`
    window.open(mapUrl, '_blank', 'noopener,noreferrer')
  }
}

const maroochydoreAddress = 'Ground Level 5/55 Plaza Parade, Maroochydore QLD 4558'
const maroochydoreCoordinates = '-26.657529591547217, 153.08437335189674'

const tocItems = [
  { id: 'who-this-clinic-is-for', title: 'Who This Clinic Is For' },
  { 
    id: 'what-we-help-with', 
    title: 'What We Help With',
    children: [
      { id: 'fertility-preconception', title: 'Fertility & Preconception Support' },
      { id: 'pregnancy-postpartum', title: 'Pregnancy & Postpartum' },
      { id: 'menstrual-hormonal', title: 'Menstrual & Hormonal Health' },
      { id: 'stress-sleep-digestion', title: 'Stress, Sleep & Digestive Balance' },
      { id: 'ongoing-womens-health', title: 'Ongoing Women\'s Health & Long-Term Care' },
    ]
  },
  { 
    id: 'what-to-expect', 
    title: 'What to Expect',
    children: [
      { id: 'your-first-visit', title: 'Your First Visit' },
      { id: 'ongoing-care', title: 'Ongoing Care' },
    ]
  },
  { id: 'location-access', title: 'Location & Access' },
  { id: 'how-to-book-fees', title: 'How to Book & Fees' },
  { id: 'also-practising', title: 'Also Practising' },
]

export default function MaroochydoreClinicPage() {
  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Acupuncture & Chinese Medicine in Maroochydore | Raintree Chinese Medicine</title>
        <meta name="description" content="Gentle, continuous care for cycles, fertility, pregnancy and perimenopause – with acupuncture, Chinese herbal medicine and nervous-system support in Maroochydore." />
        <meta property="og:title" content="Acupuncture & Chinese Medicine in Maroochydore | Raintree Chinese Medicine" />
        <meta property="og:description" content="Gentle, continuous care for cycles, fertility, pregnancy and perimenopause – with acupuncture, Chinese herbal medicine and nervous-system support in Maroochydore." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/clinics/maroochydore" />
      </Helmet>
      <h1 className="sr-only">Acupuncture & Chinese Medicine in Maroochydore</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Acupuncture & Chinese Medicine in Maroochydore for Women's Health, Fertility & Pregnancy
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Calm, unhurried acupuncture and Chinese medicine in Maroochydore, supporting women through cycles, fertility, pregnancy and hormonal changes.
              </p>
              {/* Map card */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
                <div className="w-full h-48 overflow-hidden relative bg-gray-200">
                  {maroochydoreCoordinates ? (
                    <img
                      src={getMapboxStaticImageUrl(maroochydoreCoordinates, 1200, 300)}
                      alt="Map showing location of Maroochydore clinic"
                      className="w-full h-full object-cover"
                      style={{ filter: 'grayscale(50%) contrast(1.1)' }}
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <MapPin className="h-8 w-8" />
                    </div>
                  )}
                  <button
                    onClick={() => openDirections(maroochydoreAddress, maroochydoreCoordinates)}
                    className="absolute bottom-2 right-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md text-xs font-medium shadow-lg flex items-center gap-1.5 transition-colors z-10"
                    aria-label="Get directions to Maroochydore clinic"
                  >
                    <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Get Directions</span>
                  </button>
                </div>
                <div className="p-4">
                  <p className="text-gray-700 font-medium mb-4 text-left">{maroochydoreAddress}</p>
                  <Button
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('maroochydore_hero')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Maroochydore Clinic
                    </span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Acupuncture & Chinese Medicine in Maroochydore for Women's Health, Fertility & Pregnancy
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                    Calm, unhurried acupuncture and Chinese medicine in Maroochydore, supporting women through cycles, fertility, pregnancy and hormonal changes.
                </p>
              </div>
              {/* Map card */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="w-full h-64 overflow-hidden relative bg-gray-200">
                  {maroochydoreCoordinates ? (
                    <img
                      src={getMapboxStaticImageUrl(maroochydoreCoordinates, 1200, 400)}
                      alt="Map showing location of Maroochydore clinic"
                      className="w-full h-full object-cover"
                      style={{ filter: 'grayscale(50%) contrast(1.1)' }}
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <MapPin className="h-8 w-8" />
                    </div>
                  )}
                  <button
                    onClick={() => openDirections(maroochydoreAddress, maroochydoreCoordinates)}
                    className="absolute bottom-2 right-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md text-xs font-medium shadow-lg flex items-center gap-1.5 transition-colors z-10"
                    aria-label="Get directions to Maroochydore clinic"
                  >
                    <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Get Directions</span>
                  </button>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 font-medium mb-4">{maroochydoreAddress}</p>
                  <Button
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('maroochydore_hero')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Maroochydore Clinic
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This Clinic Is For Section */}
      <section id="who-this-clinic-is-for" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Who This Clinic Is For
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left">
                This clinic is for women who want steady, long-term support rather than quick fixes – especially around:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Trying to conceive (naturally or with IVF/ICSI), feeling tired by the process and wanting a calm, informed ally.</li>
                <li>Pregnancy and postpartum, seeking safe, experienced support for symptoms, birth preparation and recovery.</li>
                <li>Menstrual and hormonal changes, from first periods through to perimenopause, wanting cycles to feel more understandable and manageable.</li>
                <li>Stress, sleep and digestion issues sitting underneath everything else, especially for carers and busy professionals who feel overloaded.</li>
                <li>Long-term patients who value continuity and want to keep working with the same practitioner across different life stages.</li>
              </ul>
              <p className="text-gray-600 text-left">
                If you recognise yourself in any of these, this clinic is designed with you in mind.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Help With at Maroochydore Section */}
      <section id="what-we-help-with" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              What We Help With at Maroochydore
            </h2>
            
            {/* Fertility & Preconception Support */}
            <div id="fertility-preconception" className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Fertility & Preconception Support
                </h3>
                <p className="text-gray-600 mb-4 text-left">
                  For people trying to conceive – with or without IVF – this clinic offers acupuncture, Chinese herbal medicine and practical support aimed at:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Working alongside fertility treatment, not instead of it.</li>
                  <li>Paying attention to cycles, sleep, digestion and stress.</li>
                  <li>Helping you feel more steadied, informed and supported through a demanding process.</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Sessions are always designed to sit alongside the care you receive from your GP, specialist or IVF team.
                </p>
              </div>
            </div>

            {/* Pregnancy & Postpartum */}
            <div id="pregnancy-postpartum" className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Pregnancy & Postpartum
                </h3>
                <p className="text-gray-600 mb-4 text-left">
                  This clinic provides safe, gentle care during all trimesters and into the postpartum months, with support that may help with:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Common pregnancy symptoms such as nausea, sleep disturbance, discomfort and anxiety.</li>
                  <li>Birth preparation and late-pregnancy comfort.</li>
                  <li>Postnatal recovery, energy and emotional adjustment in the "fourth trimester".</li>
                </ul>
                <p className="text-gray-600 text-left">
                  The focus is on keeping you as comfortable and supported as possible, while working in with your midwife, GP or obstetric team.
                </p>
              </div>
            </div>

            {/* Menstrual & Hormonal Health */}
            <div id="menstrual-hormonal" className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Menstrual & Hormonal Health
                </h3>
                <p className="text-gray-600 mb-4 text-left">
                  Many people come for support with:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Painful, heavy or irregular periods.</li>
                  <li>PMS/PMDD, mood changes around the cycle.</li>
                  <li>Perimenopausal symptoms such as sleep changes, hot flushes, mood shifts or cycle changes.</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Appointments are a space to make sense of what is happening, join the dots between hormones, stress, sleep and digestion, and work towards cycles that feel more predictable and manageable over time.
                </p>
              </div>
            </div>

            {/* Stress, Sleep & Digestive Balance */}
            <div id="stress-sleep-digestion" className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Stress, Sleep & Digestive Balance
                </h3>
                <p className="text-gray-600 mb-4 text-left">
                  Stress, sleep and digestion are treated as core to almost every case.
                </p>
                <p className="text-gray-600 mb-4 text-left">
                  Acupuncture and Chinese medicine may help with:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Feeling constantly "on" or wired but tired.</li>
                  <li>Trouble getting to sleep or staying asleep.</li>
                  <li>IBS-type symptoms, bloating or unsettled digestion linked to stress.</li>
                  <li>A general sense of burnout or depletion.</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Each session offers time to pause, regulate and reset, while gently rebuilding resilience over time.
                </p>
              </div>
            </div>

            {/* Ongoing Women's Health & Long-Term Care */}
            <div id="ongoing-womens-health">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Ongoing Women's Health & Long-Term Care
                </h3>
                <p className="text-gray-600 mb-4 text-left">
                  Many people choose to stay over several life stages – for example, moving from cycle issues into fertility, pregnancy, postpartum recovery and later perimenopause.
                </p>
                <p className="text-gray-600 mb-4 text-left">
                  This clinic offers:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Long-term, relationship-based care.</li>
                  <li>A practitioner who remembers your story and adjusts treatment as your body and life change.</li>
                  <li>Space to review, refine and shift focus as new phases arise.</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA15Min onBook={openBooking} />

      {/* What to Expect Section */}
      <section id="what-to-expect" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              What to Expect
            </h2>
            
            {/* Your First Visit */}
            <div id="your-first-visit" className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Your First Visit
                </h3>
                <p className="text-gray-600 mb-4 text-left">
                  At your first appointment, there is time to:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Talk through your main concerns and your health history in detail.</li>
                  <li>Look at cycles, sleep, stress, digestion and overall context – not just one symptom in isolation.</li>
                  <li>Discuss what is realistic for you in terms of frequency and support.</li>
                  <li>Begin treatment with acupuncture, and, if appropriate, discuss Chinese herbal medicine and simple lifestyle suggestions.</li>
                </ul>
                <p className="text-gray-600 text-left">
                  The aim is for you to leave feeling heard and with a clearer sense of direction.
                </p>
              </div>
            </div>

            {/* Ongoing Care */}
            <div id="ongoing-care">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Ongoing Care
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Ongoing appointments focus on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Reviewing what has changed since your last visit.</li>
                  <li>Adjusting treatment to where you are in your cycle, fertility treatment or pregnancy timeline.</li>
                  <li>Continuing to support the nervous system, digestion and sleep as things evolve.</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Plans are always collaborative and flexible – built to fit around work, family and medical care, rather than adding pressure.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Location & Access Section */}
      <section id="location-access" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Location & Access
            </h2>
            
            {/* Mobile: Stack layout */}
            <div className="lg:hidden">
              <div className="pl-4 max-w-3xl mx-auto">
                <p className="text-gray-600 mb-4 text-base font-medium">
                  The Maroochydore clinic is located at:
                </p>
                <p className="text-gray-700 mb-6 text-left font-medium">
                  {maroochydoreAddress}
                </p>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Access details:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Parking: Easy off street parking available.</li>
                  <li>Location: Kon-Tiki Building, opposite Maroochydore Plaza.</li>
                  <li>Accessibility: Ground level clinic with easy access.</li>
                </ul>
                <p className="text-gray-600 text-left mb-6">
                  You'll receive full details and directions in your confirmation email after you book.
                </p>
              </div>
              {/* Map card */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="w-full h-48 overflow-hidden relative bg-gray-200">
                  {maroochydoreCoordinates ? (
                    <img
                      src={getMapboxStaticImageUrl(maroochydoreCoordinates, 1200, 300)}
                      alt="Map showing location of Maroochydore clinic"
                      className="w-full h-full object-cover"
                      style={{ filter: 'grayscale(50%) contrast(1.1)' }}
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <MapPin className="h-8 w-8" />
                    </div>
                  )}
                  <button
                    onClick={() => openDirections(maroochydoreAddress, maroochydoreCoordinates)}
                    className="absolute bottom-2 right-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md text-xs font-medium shadow-lg flex items-center gap-1.5 transition-colors z-10"
                    aria-label="Get directions to Maroochydore clinic"
                  >
                    <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Get Directions</span>
                  </button>
                </div>
                <div className="p-4">
                  <p className="text-gray-700 font-medium mb-4 text-left">{maroochydoreAddress}</p>
                  <Button
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('maroochydore_location')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Maroochydore Clinic
                    </span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <p className="text-gray-600 mb-4 text-base font-medium">
                  The Maroochydore clinic is located at:
                </p>
                <p className="text-gray-700 mb-6 text-left font-medium">
                  {maroochydoreAddress}
                </p>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Access details:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Parking: Easy off street parking available.</li>
                  <li>Location: Kon-Tiki Building, opposite Maroochydore Plaza.</li>
                  <li>Accessibility: Ground level clinic with easy access.</li>
                </ul>
                <p className="text-gray-600 text-left">
                  You'll receive full details and directions in your confirmation email after you book.
                </p>
              </div>
              {/* Map card */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="w-full h-64 overflow-hidden relative bg-gray-200">
                  {maroochydoreCoordinates ? (
                    <img
                      src={getMapboxStaticImageUrl(maroochydoreCoordinates, 1200, 400)}
                      alt="Map showing location of Maroochydore clinic"
                      className="w-full h-full object-cover"
                      style={{ filter: 'grayscale(50%) contrast(1.1)' }}
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <MapPin className="h-8 w-8" />
                    </div>
                  )}
                  <button
                    onClick={() => openDirections(maroochydoreAddress, maroochydoreCoordinates)}
                    className="absolute bottom-2 right-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md text-xs font-medium shadow-lg flex items-center gap-1.5 transition-colors z-10"
                    aria-label="Get directions to Maroochydore clinic"
                  >
                    <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Get Directions</span>
                  </button>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 font-medium mb-4">{maroochydoreAddress}</p>
                  <Button
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('maroochydore_location')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Maroochydore Clinic
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Book & Fees Section */}
      <section id="how-to-book-fees" className="py-20 bg-white">
        <div className="container mx-auto px-4">
         <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              How to Book & Fees
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-gray-600 mb-8 text-center">
                Appointments at the Maroochydore clinic can be booked online any time.
              </p>
            </div>
         </motion.div>
         {/* Appointment Cards */}
             <MaroochydoreAppointmentCards 
                  appointments={getGeneralHealthAppointments().filter(appt => 
                    appt.title !== "Free 15min Phone Consult"
                  )} 
                  onBook={(source) => openBooking(`maroochydore_fees_${source || 'card'}`)}
                />
         <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="text-center mt-8 lg:mt-12"
            >
            <p className="text-gray-600 mb-1 text-center">
                For the most up-to-date fee information and available times, please visit the{' '}
                <Link to="/appointments" className="underline text-gray-600 hover:text-gray-800">
                  Appointments page
                </Link>.
              </p>
              <p className="text-gray-600 mb-6 text-center">
                If you're unsure which appointment type to choose, you're welcome to get in touch first and ask.
              </p>
            </motion.div>
        </div>
      </section>

      {/* Also Practising at Tewantin Section */}
      <section id="also-practising" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <h2 className="text-2xl md:text-3xl mb-4 text-[#3f4141] text-left">
                Also Practising at Tewantin
              </h2>
              <p className="text-gray-600 mb-4 text-left">
                The same style of care is also available at Noosa clinic.
              </p>
              <p className="text-gray-600 mb-4 text-left">
                If Noosa is more convenient for you, you can read more and book there instead:
              </p>
              <Link
                to="/clinics/noosa"
                className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
              >
                View Tewantin Clinic
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  )
}

